/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function isValidUrl(url) {
    try {
        new URL(url); // eslint-disable-line no-new
    } catch (e) {
        return false;
    }
    return true;
}

function isNodeEnv() {
    return typeof window === 'undefined';
}

function isProduction() {
    return process.env.NODE_ENV === 'production';
}

function isLocal() {
    return !isProduction();
}

function isCI() {
    return process.env.CI === 'true';
}

const DefaultNullReturn = () => null;
/**
 * The function `createNullReturnModule` creates a proxy object that returns a default value or a
 * `NullFunction` when accessing properties of a module with an error.
 * @param options - The `options` parameter in the `createNullReturnModule` function is an object that
 * contains the following properties:
 * @returns The `createNullReturnModule` function returns a new Proxy object that wraps the provided
 * module object. The Proxy intercepts property access on the module object and handles it based on the
 * logic defined in the `get` trap function. If the property being accessed is 'then', it returns
 * `undefined` to prevent infinite resolution in Promise contexts. Otherwise, it logs an error message
 * indicating that an attempt was
 */
function createNullReturnModule(reference, error, module) {
    return new Proxy(module !== null && module !== void 0 ? module : {}, {
        get: (target, property) => {
            // This is to prevent infinite resolution when being used in "Promise" context
            // such as a resolved value of a promise or a return value from async function.
            // Put this "if" before loggin as it is not helpful in logging "then" use case
            if (property === 'then') {
                return undefined;
            }
            console.error(`Try to resolve named export "${property.toString()}" from module "${reference}" which has an error.`, 'originalError:', error);
            // use the default value if it exists
            // otherwise assume it's a function and return the NullFunction
            // since the main use case is to replce a React functional component to render nothing.
            return target[property] || DefaultNullReturn;
        },
    });
}

function convertToFormattedString(executionContexts, module) {
    if (!executionContexts || executionContexts.length === 0) {
        return '';
    }
    let found = false;
    const remoteContextString = executionContexts
        .filter((current) => {
        if (!found && module && module === current.module) {
            found = true;
            return true;
        }
        return !found;
    })
        .map((c) => c.module)
        .map(formatRemote)
        .reverse()
        .join(' < ');
    const dynamicContextString = convertDynamicContext(executionContexts[0].dynamicContext);
    return concat(remoteContextString, dynamicContextString);
}
function convertDynamicContext(dynamicContexts) {
    if (!dynamicContexts || dynamicContexts.length === 0) {
        return '';
    }
    const lastDynamicContext = dynamicContexts[dynamicContexts.length - 1];
    const remainingRemoteContextString = convertToFormattedString(lastDynamicContext.importRemoteContext, lastDynamicContext.module);
    return concat(formatDynamic(lastDynamicContext.module), remainingRemoteContextString);
}
function formatRemote(s) {
    return `remote(${s})`;
}
function formatDynamic(s) {
    return `dynamic(${s})`;
}
function concat(child, parent) {
    if (!parent) {
        return child;
    }
    return `${child} < ${parent}`;
}

const IMPORT_REMOTE_SINGLETON_NAMESPACE = 'spt.importRemoteSingleton';
/**
 * Returns the global import remote context
 * @returns {Object | undefined} AsyncLocalstorage or undefined
 */
/* istanbul ignore next */
function getImportRemoteContext() {
    if (isNodeEnv()) {
        return global[Symbol.for(IMPORT_REMOTE_SINGLETON_NAMESPACE)];
    }
    return undefined;
}
/**
 * Fetch imprort remote relevant data from importRemoteContext
 * @param {String} moduleName
 * @returns {Object} { env, hasOverrideCookie, versionOverride }
 */
function getDataFromRemoteContext(moduleName) {
    const importRemoteContext = getImportRemoteContext();
    if (typeof importRemoteContext !== 'undefined') {
        const { getModuleVersionOverride, getVersionOverrides, env, pushExecutionContext, popExecutionContext, pushDynamicExecutionContext, popDynamicExecutionContext, pushManifestContext, setDynamicExecutionContextSnapshot, } = importRemoteContext;
        const hasOverrideCookie = moduleName && Boolean(getVersionOverrides === null || getVersionOverrides === void 0 ? void 0 : getVersionOverrides());
        const versionOverride = moduleName && hasOverrideCookie
            ? getModuleVersionOverride === null || getModuleVersionOverride === void 0 ? void 0 : getModuleVersionOverride(moduleName)
            : undefined;
        return {
            env,
            hasOverrideCookie,
            versionOverride,
            pushExecutionContext,
            popExecutionContext,
            pushDynamicExecutionContext,
            popDynamicExecutionContext,
            pushManifestContext,
            setDynamicExecutionContextSnapshot,
        };
    }
    return {};
}
/** doing deep copy to prevent accidental update outside its use */
function getExecutionContext() {
    var _a;
    const importRemoteContext = getImportRemoteContext();
    const result = deepCopy(((_a = importRemoteContext === null || importRemoteContext === void 0 ? void 0 : importRemoteContext.getExecutionContext) === null || _a === void 0 ? void 0 : _a.call(importRemoteContext)) || []);
    return result;
}
/** doing deep copy to prevent accidental update outside its use */
function getDynamicExecutionContextSnapshot() {
    var _a;
    const importRemoteContext = getImportRemoteContext();
    const result = deepCopy(((_a = importRemoteContext === null || importRemoteContext === void 0 ? void 0 : importRemoteContext.getDynamicExecutionContextSnapshot) === null || _a === void 0 ? void 0 : _a.call(importRemoteContext)) || []);
    return result;
}
function getExecutionContextString(module) {
    return convertToFormattedString(getExecutionContext(), module);
}
/* eslint-disable @typescript-eslint/no-explicit-any */
function deepCopy(json) {
    return JSON.parse(JSON.stringify(json));
}

/**
 * @typedef {"manifestEntryNotFound" | "manifestNetwork" | "remoteEntryExecution" | "remoteEntryModuleNotFound" | "remoteEntryNetwork" | "remoteEntryNotFound" | "remoteEntryValidation" |  * } ImportRemoteErrorType
 */

const IMPORT_REMOTE_ERROR_TYPES = {
    manifestEntryNotFound: 'manifestEntryNotFound',
    manifestNetwork: 'manifestNetwork',
    remoteEntryExecution: 'remoteEntryExecution',
    remoteEntryModuleNotFound: 'remoteEntryModuleNotFound',
    remoteEntryNetwork: 'remoteEntryNetwork',
    remoteEntryNotFound: 'remoteEntryNotFound',
    remoteEntryValidation: 'remoteEntryValidation',
};

/**
 * The `ImportRemoteError` class is a custom error class in JavaScript
 * that represents an error that occurs when importing a remote resource,
 * with options for specifying the cause and type of the error.
 * Allows easier tracing and can be extended in the future to include identifier tags.
 * @extends Error
 * @property {Error} cause The reason the error exists.
 * @property {ImportRemoteErrorType} type The type of error that happened. Defaults to `network`.
 */
class ImportRemoteError extends Error {
    type;

    cause;

    executionContext;

    /**
     *
     * @param {String} message Error message
     * @param {Object} [options] Error options containing:
     * @param {Error} [options.cause] The reason the error exists.
     * @param {ImportRemoteErrorType} [options.type] The type of error that happened. Defaults to `network`.
     * * @param {String} [options.executionContext] The context of error that happened in remote/dynamic import. Defaults to empty string.
     * @return {ImportRemoteError} Import Remote Error.
     */
    constructor(
        message,
        { cause, type = 'network', executionContext = '' } = {},
    ) {
        // In Node 14, Error constructor does not have options, available in Node 18 though
        // https://nodejs.org/docs/latest-v14.x/api/errors.html#errors_new_error_message
        // https://nodejs.org/docs/latest-v18.x/api/errors.html#new-errormessage-options
        super(message);
        this.type = type;
        this.cause = cause;
        this.executionContext = executionContext;
    }
}

/**
 * Checks if an error is an `ImportRemoteError` and returns a new `ImportRemoteError` instance accordingly.
 * @param error - Takes an `error` parameter and processes it to return an instance of `ImportRemoteError`.
 * @returns Instance of `ImportRemoteError`. If the input `error` is an instance of `ImportRemoteError`,
 * it returns the same error. If the input `error` is an instance of `Error`, it creates a new
 * `ImportRemoteError` with a message indicating it is an uncaught error and sets the original error as the
 * cause. Otherwise, it creates a new error from the value sent.
 */
function getUncoughtError(error) {
    if (error instanceof ImportRemoteError) {
        // get cought error
        return error;
    }
    if (error instanceof Error) {
        // get uncought error
        return new ImportRemoteError(`uncaught error: ${error.message}`, {
            cause: error,
            executionContext: getExecutionContextString(),
        });
    }

    // wrap unknown value in an error
    const fixedError = new Error(String(error));
    return new ImportRemoteError(`uncaught error: ${fixedError.message}`, {
        cause: fixedError,
        executionContext: getExecutionContextString(),
    });
}

const SHOPPER_PLATFORM_URLS = {
    local: ['http://localhost:8000', 'https://localhost:8001'],
    dev: [
        'https://web-platform-traffic-controller.int.zillow-dev-k8s.zg-int.net',
        'https://www.sp1.test-automation.zillow.net/shopper-platform',
    ],
    test: [
        'https://web-platform-traffic-controller.int.zillow-test-k8s.zg-int.net',
        'https://www.qa.zillow.net/shopper-platform',
    ],
    production: [
        'https://web-platform-traffic-controller.int.zillow-prod-k8s.zg-int.net',
        'https://www.zillow.com/shopper-platform',
    ],
};

const { Agent: HttpAgent } = isNodeEnv() ? require('http') : {};
const { Agent: HttpsAgent } = isNodeEnv() ? require('https') : {};

const httpAgent = HttpAgent
    ? new HttpAgent({ keepAlive: true, maxSockets: 256 })
    : undefined;
const httpsAgent = HttpsAgent
    ? new HttpsAgent({ keepAlive: true, maxSockets: 256 })
    : undefined;
const HTTP_PROTOCOL = 'http:';
const HTTPS_PROTOCOL = 'https:';

function getManifestUrl(subApp, env = 'production', version, moduleReference) {
    const baseUrlPaths = SHOPPER_PLATFORM_URLS[env];
    if (!baseUrlPaths) {
        throw new ImportRemoteError(
            `no shopper-platform base url found for environment: "${env}". Must be one of: ${Object.keys(
                SHOPPER_PLATFORM_URLS,
            ).join(',')}`,
            {
                executionContext: getExecutionContextString(moduleReference),
            },
        );
    }
    const baseUrl = isNodeEnv() ? baseUrlPaths[0] : baseUrlPaths[1];

    return version
        ? // Must NOT have trailing slash due to gateway
          `${baseUrl}/api/sub-app/manifest/${subApp}/${version}`
        : // Must have trailing slash due to how Zillow routes traffic
          `${baseUrl}/api/sub-app/manifest/effective-version/${subApp}/`;
}

function isProperObjectOverride(version) {
    return (
        !Array.isArray(version) &&
        typeof version === 'object' &&
        version !== null &&
        version.remoteEntryClientUrl &&
        version.remoteEntryServerUrl
    );
}

/**
 * Fetches sub-app manifest from shopper-platform.
 * @param {string} subAppName
 * @param {string} [environment]
 * @param {string | string[] | { remoteEntryClientUrl: string; remoteEntryServerUrl: string; }} [version]
 * @return {Promise<{ remoteEntryClientUrl: string, remoteEntryServerUrl: string, scope: string }>}
 */
async function fetchSubAppManifest(
    subAppName,
    environment = 'production',
    version,
    moduleReference = undefined,
) {
    const versionIsLocalOverride =
        Array.isArray(version) && version.length === 2;
    const versionIsObjectOverride = isProperObjectOverride(version);
    const subAppManifestUrl = getManifestUrl(
        subAppName,
        environment,
        typeof version === 'string' ? version : undefined,
        moduleReference,
    );

    try {
        // http(s) agent is used to utilize connection pooling to request the manifest
        const { protocol } = new URL(subAppManifestUrl);
        let agent;
        if (protocol === HTTP_PROTOCOL) {
            agent = httpAgent;
        } else if (protocol === HTTPS_PROTOCOL) {
            agent = httpsAgent;
        }

        const response = await fetch(subAppManifestUrl, { agent });

        if (!response?.ok) {
            const res = await response.json();
            const { message } = res;
            if (response.status === 404) {
                throw new ImportRemoteError(
                    `sub-app manifest 404/not found. sub-app manifest URL: ${subAppManifestUrl}`,
                    {
                        cause: new Error(message),
                        type: IMPORT_REMOTE_ERROR_TYPES.manifestEntryNotFound,
                        executionContext:
                            getExecutionContextString(moduleReference),
                    },
                );
            }
            throw new ImportRemoteError(
                `sub-app manifest fetch response came back not OK. sub-app manifest URL: ${subAppManifestUrl}, status: ${response.status}`,
                {
                    cause: new Error(message),
                    type: IMPORT_REMOTE_ERROR_TYPES.manifestNetwork,
                    executionContext:
                        getExecutionContextString(moduleReference),
                },
            );
        }

        const data = await response.json();

        if (versionIsLocalOverride) {
            data.remoteEntryClientUrl = version[0];
            data.remoteEntryServerUrl = version[1];
            data.isLocalOverride = true;
        } else if (versionIsObjectOverride) {
            return {
                ...data,
                ...version,
                isOverride: true,
            };
        }

        return data;
    } catch (error) {
        if (error instanceof ImportRemoteError) {
            throw error;
        }
        throw new ImportRemoteError(
            `failed to fetch or parse sub-app manifest. URL: ${subAppManifestUrl}`,
            {
                cause: error,
                executionContext: getExecutionContextString(moduleReference),
            },
        );
    }
}

function getEffectiveVersionFromManifest(subAppName, manifest) {
    const remoteEntryUrls =
        manifest?.[subAppName]?.versions?.slice(-1)[0] || null;
    if (!remoteEntryUrls) {
        return null;
    }
    const effectiveVersionResponse = {
        ...remoteEntryUrls,
        ...manifest[subAppName],
    };
    delete effectiveVersionResponse.versions;
    if (!effectiveVersionResponse.scope) {
        // scope is needed
        return null;
    }
    return effectiveVersionResponse;
}

/**
 * @typedef ModuleDetails
 * @type {Object}
 * @property {String} moduleName The module name.
 * @property {String} modulePath The module relative path.
 */

/**
 * The function `getModuleDetails` takes a module reference as input and returns an object with the
 * module name and a relative module path.
 * @param {String} moduleReference - The `moduleReference` parameter is a string that represents the reference
 * to a module in the format `${subAppName}/${module}`
 * @returns {ModuleDetails} moduleDetails - An object with the module details is being returned.
 */
function getModuleDetails(moduleReference) {
    // Separate the moduleName from the module path
    const [moduleName, ...moduleParts] = moduleReference.split('/');

    return {
        moduleName,
        // Join the module path back together and prefix with "./" to build a relative path
        modulePath: `./${moduleParts.join('/')}`,
    };
}

function isVersionOverrideSingle(version) {
    return typeof version === 'string';
}
function isVersionOverridePair(version) {
    if (!Array.isArray(version)) {
        return false;
    }
    if (version.length !== 2) {
        return false;
    }
    return (isVersionOverrideSingle(version[0]) &&
        isVersionOverrideSingle(version[1]));
}
function isVersionOverrideObject(version) {
    if (!version) {
        return false;
    }
    if (typeof version !== 'object') {
        return false;
    }
    if (!('remoteEntryClientUrl' in version) ||
        !('remoteEntryServerUrl' in version)) {
        return false;
    }
    if (Object.keys(version).length > 2) {
        return false;
    }
    return (isVersionOverrideSingle(version.remoteEntryClientUrl) &&
        isVersionOverrideSingle(version.remoteEntryServerUrl));
}
function isVersionOverride(version) {
    return (isVersionOverrideSingle(version) ||
        isVersionOverridePair(version) ||
        isVersionOverrideObject(version));
}

const WINDOW_MANIFEST_CACHE_KEY = 'shopperPlatformManifest';
function getRemoteManifestOnClient(moduleReference_1) {
    return __awaiter(this, arguments, void 0, function* (moduleReference, { env, version } = {}) {
        const { moduleName } = getModuleDetails(moduleReference);
        return getManifest$1(moduleName, env, version);
    });
}
function remoteOnClient(moduleReference_1, _a) {
    return __awaiter(this, arguments, void 0, function* (moduleReference, { env, version }) {
        if (!window.importedRemotes) {
            window.importedRemotes = {};
        }
        if (window && window.importedRemotes[moduleReference]) {
            return window.importedRemotes[moduleReference];
        }
        const { moduleName, modulePath } = getModuleDetails(moduleReference);
        const { remoteEntryClientUrl, scope } = yield getManifest$1(moduleName, env, version);
        const module = yield loadModuleOnClient({
            remoteEntryUrl: remoteEntryClientUrl,
            scope,
            modulePath,
        });
        window.importedRemotes[moduleReference] = module;
        return module;
    });
}
function getManifest$1(moduleName, env, version) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!window.shopperPlatformCache) {
            window.shopperPlatformCache = {};
        }
        let manifest;
        if (window && window.shopperPlatformCache[moduleName]) {
            manifest = window.shopperPlatformCache[moduleName];
        }
        else if (!isVersionOverride(version)) {
            // we cannot ensure the version override for this request is in the injected manifest,
            // so we do not attempt to resolve from the cached manifest if we have a local override
            manifest = getEffectiveVersionFromManifest(moduleName, window[WINDOW_MANIFEST_CACHE_KEY]);
        }
        if (!manifest) {
            manifest = yield fetchSubAppManifest(moduleName, env, version);
        }
        window.shopperPlatformCache[moduleName] = manifest;
        return manifest;
    });
}
function getContainer(_a) {
    return __awaiter(this, arguments, void 0, function* ({ scope, remoteEntryUrl }) {
        const dynamicWindow = window;
        const containerPromiseKey = `${scope}_p`;
        if (!dynamicWindow[containerPromiseKey]) {
            dynamicWindow[containerPromiseKey] = new Promise((resolve, reject) => {
                const element = document.createElement('script');
                element.src = remoteEntryUrl;
                element.type = 'text/javascript';
                element.async = true;
                element.onload = () => {
                    // Initializes the share scope.
                    // This fills it with known provided modules from this build and all remotes
                    Promise.resolve(__webpack_init_sharing__('default')).then(() => resolve(dynamicWindow[scope]));
                };
                element.onerror = (error) => {
                    var _a;
                    const importRemoteError = new ImportRemoteError(`failed to inject remote entry script into document. URL: ${remoteEntryUrl}`, {
                        type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryExecution,
                        cause: error,
                    });
                    (_a = dynamicWindow.DD_RUM) === null || _a === void 0 ? void 0 : _a.onReady(() => {
                        var _a;
                        const reportedError = new Error(importRemoteError.message);
                        reportedError.name = `ImportRemoteError`;
                        reportedError.cause = error;
                        (_a = dynamicWindow.DD_RUM) === null || _a === void 0 ? void 0 : _a.addError(reportedError);
                    });
                    reject(importRemoteError);
                };
                document.head.appendChild(element);
            });
        }
        return dynamicWindow[containerPromiseKey];
    });
}
function loadModuleOnClient(_a) {
    return __awaiter(this, arguments, void 0, function* ({ modulePath, remoteEntryUrl, scope, }) {
        const dynamicWindow = window;
        const container = yield getContainer({ remoteEntryUrl, scope });
        if (!container) {
            throw new ImportRemoteError(`could not load remote container. The remote script failed to load, or the remote script did not add itself to the window as expected or the scope is invalid. scope: ${scope}, modulePath: ${modulePath}`, { type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryModuleNotFound });
        }
        // Initialize the container, it may provide shared modules
        try {
            yield container.init(__webpack_share_scopes__.default);
            const factory = yield dynamicWindow[scope].get(modulePath);
            const Module = factory();
            return Module;
        }
        catch (error) {
            // Throw this error as its own type so Platform is aware that the scope and module name combination
            // does not exist. Error message: Module "./ModuleName" does not exist in container.
            if (error instanceof Error &&
                (error === null || error === void 0 ? void 0 : error.message) &&
                error.message.includes('does not exist in container.')) {
                throw new ImportRemoteError(`failed to initialize remote module. Module not found in container. scope: ${scope}, moduleName/path: "${modulePath}" .URL: ${remoteEntryUrl}`, {
                    type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryModuleNotFound,
                    cause: error,
                });
            }
            throw new ImportRemoteError(`failed to initialize remote container or module. scope: ${scope}, modulePath: ${modulePath}`, {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryExecution,
                cause: error,
            });
        }
    });
}

let memoryCacheModule = {};

if (isNodeEnv()) {
    memoryCacheModule = require('memory-cache');
}

const memoryCache = memoryCacheModule;

let moduleCache = {};
if (isNodeEnv()) {
    const { moduleCache: GlobalModuleCache } = getImportRemoteContext() ?? {};
    if (GlobalModuleCache) {
        moduleCache = GlobalModuleCache;
    } else {
        // TODO: Remove this once we can confirm guaranteed availability of the GlobalModuleCache
        console.warn(
            '[ImportRemoteModuleCache] GlobalModuleCache not found. Falling back to importRemote instance.',
        );
        const { Cache } = memoryCache;
        moduleCache = new Cache();
    }
}
const localCache = moduleCache;

/**
 * The function `getModuleCacheKey` returns a cache key based on the remote entry URL and module name.
 * @param {String} remoteEntryUrl - The URL of the remote entry file that contains the module.
 * @param {String} moduleName - The `moduleName` parameter is a string that represents the name of a module.
 * @returns a cache key string if both the `remoteEntryUrl` and `moduleName` parameters are provided.
 * Otherwise, it returns `null`.
 */
function getModuleCacheKey(remoteEntryUrl, moduleName) {
    if (remoteEntryUrl && moduleName) {
        return `${remoteEntryUrl}:${moduleName}`;
    }
    return null;
}

/**
 * The function `getContainerCacheKey` returns a cache key for a container module based on the remote
 * entry URL and module name.
 * @param {String} remoteEntryUrl - The `remoteEntryUrl` parameter is a string that represents the URL of a
 * remote entry file. This file is typically used in a module federation setup to dynamically load and
 * share modules between different applications.
 * @returns {String} a cache key string if the remoteEntryUrl parameter is provided.
 * Otherwise, it returns `null`.
 */
function getContainerCacheKey(remoteEntryUrl) {
    if (remoteEntryUrl) {
        return `container:${getModuleCacheKey(remoteEntryUrl, 'all')}`;
    }
    return null;
}

// Invalidate cached modules after 1 minute
const DEFAULT_MODULE_CACHE_TTL_MS = 1000 * 60;

// We want to avoid making a request for the manifest every request
// but also want updates to the DB to happen fairly immediately
const TWENTY_FIVE_SECONDS_IN_MS = 1000 * 25;
const ONE_MINUTES_TTL_MS = 1000 * 60;
const FIVE_MINUTES_TTL_MS = 1000 * 60 * 5;
const FIVE_SECONDS_UPDATE_LOOP_MS = 1000 * 5;

const MODULES_TO_UPDATE = {};

async function getRemoteManifestOnServer(
    moduleReference,
    { env, version } = {},
) {
    const { moduleName } = getModuleDetails(moduleReference);
    const { versionOverride, env: envFromRequest } =
        getDataFromRemoteContext(moduleName);
    const finalEnv = env ?? envFromRequest ?? 'production';
    const finalVersion = version ?? versionOverride;

    return getManifest(moduleName, finalEnv, finalVersion, moduleReference);
}

async function remoteOnServer(
    moduleReference,
    { env, version, moduleTTL } = {},
) {
    const { moduleName, modulePath } = getModuleDetails(moduleReference);
    const {
        hasOverrideCookie,
        versionOverride,
        env: envFromRequest,
        pushExecutionContext,
        popExecutionContext,
        pushManifestContext,
    } = getDataFromRemoteContext(moduleName);
    const finalEnv = env ?? envFromRequest ?? 'production';
    const finalVersion = version ?? versionOverride;
    try {
        const manifest = await getManifest(
            moduleName,
            finalEnv,
            finalVersion,
            moduleReference,
        );

        const {
            remoteEntryServerUrl,
            remoteEntryClientUrl,
            scope,
            isLocalOverride = false,
            version: versionFromManifest,
        } = manifest;

        pushManifestContext?.(manifest);
        pushExecutionContext?.({
            module: moduleReference,
            dynamicContext: getDynamicExecutionContextSnapshot(),
        });
        // this "await" is required so that the "popExecutionContext" is working properly.
        return await loadModuleOnServer({
            version: versionFromManifest,
            remoteEntryUrl: remoteEntryServerUrl,
            clientRemoteEntryUrl: remoteEntryClientUrl,
            scope,
            modulePath,
            isLocalOverride,
            hasOverrideCookie,
            moduleTTL,
            moduleReference,
        });
        // return moduleResult;
    } finally {
        popExecutionContext?.();
    }
}

async function internalFetchSubAppManifest(
    cacheKey,
    moduleName,
    env,
    version,
    moduleReference,
) {
    const manifest = await fetchSubAppManifest(
        moduleName,
        env,
        version,
        moduleReference,
    );
    // If version, it is not going to change. So we don't need to update it often
    // we put TTL here to just free up the memory when it is no longer used
    localCache.put(
        cacheKey,
        manifest,
        version ? FIVE_MINUTES_TTL_MS : ONE_MINUTES_TTL_MS,
    );
    return manifest;
}

async function getManifest(moduleName, env, version, moduleReference) {
    const cacheKey = version ? `${moduleName}:${version}` : moduleName;
    let manifest = localCache.get(cacheKey);
    if (manifest && !isLocal()) {
        return manifest;
    }

    manifest = internalFetchSubAppManifest(
        cacheKey,
        moduleName,
        env,
        version,
        moduleReference,
    );

    // Only check updates for non-versioned modules
    // For non-version-module., it will only be called once on the first call.
    // (mostly during cache priming)
    MODULES_TO_UPDATE[moduleName] = true;

    return manifest;
}

/**
 * The function `getRemoteEntryResponseText` fetches a remote file from a given URL and returns its
 * response body as text.
 * @param {String} remoteEntryUrl - The `remoteEntryUrl` parameter is the
 * URL of the remote entry file that you want to fetch and retrieve
 * the response text from.
 * @returns {String} the response text of the remote entry URL.
 */
async function getRemoteEntryResponseText(remoteEntryUrl, moduleReference) {
    let remoteEntryResponse;
    const executionContext = getExecutionContextString(moduleReference);
    try {
        remoteEntryResponse = await fetch(remoteEntryUrl, {
            // The endpoint we hit to get content tries to serve compressed content
            // unless we explicitly tell it to not send compressed files. Right now
            // we aren't equipped to decompress then eval files.
            headers: {
                'Accept-Encoding': 'null',
            },
        });
        if (remoteEntryResponse?.ok === false) {
            if (remoteEntryResponse?.status === 404) {
                throw new ImportRemoteError(
                    `remote entry file 404/not found. URL: ${remoteEntryUrl}`,
                    {
                        type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryNotFound,
                        cause: new Error(remoteEntryResponse.message),
                        executionContext,
                    },
                );
            }
            throw new ImportRemoteError(
                `remote entry fetch came back not OK. URL: ${remoteEntryUrl} status:${remoteEntryResponse.status}`,
                {
                    type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryNetwork,
                    cause: new Error(remoteEntryResponse.message),
                    executionContext,
                },
            );
        }
    } catch (error) {
        if (error instanceof ImportRemoteError) {
            throw error;
        }

        throw new ImportRemoteError(
            `error encountered while fetching remote entry. URL: ${remoteEntryUrl}`,
            {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryNetwork,
                cause: error,
                executionContext,
            },
        );
    }

    let remoteEntryResponseText;
    try {
        remoteEntryResponseText = await remoteEntryResponse.text();
    } catch (error) {
        throw new ImportRemoteError(
            `failed to convert remote file response body to text. URL: ${remoteEntryUrl}`,
            {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryNetwork,
                cause: error,
                executionContext,
            },
        );
    }

    if (remoteEntryResponseText.trim().length === 0) {
        throw new ImportRemoteError(
            `empty remote entry script detected. URL: ${remoteEntryUrl}`,
            {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryExecution,
                executionContext,
            },
        );
    }

    return remoteEntryResponseText;
}

/**
 * The function `getRemoteModuleBaseUrl` takes a remote entry URL and returns
 * the base URL by removing the last segment of the URL.
 * @param {String} remoteEntryUrl - The `remoteEntryUrl` parameter is a string
 * that represents the URL of a remote entry file.
 * @returns {String} the remote module base URL.
 */
function getRemoteModuleBaseUrl(remoteEntryUrl, moduleReference) {
    const remoteModuleBaseUrl = remoteEntryUrl.substring(
        0,
        remoteEntryUrl.lastIndexOf('/'),
    );
    if (!isValidUrl(remoteModuleBaseUrl)) {
        throw new ImportRemoteError(
            `invalid remote module base URL: ${remoteModuleBaseUrl}. original remote entry URL: ${remoteEntryUrl}`,
            {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryValidation,
                executionContext: getExecutionContextString(moduleReference),
            },
        );
    }

    return remoteModuleBaseUrl;
}

/**
 * The function `evalRemoteModuleContainer` evaluates remote module code using
 * `eval()` and returns the result.
 * @param {Object} remoteModuleContainer Object with the information describing the remote.
 * @param {String} remoteModuleContainer.remoteModuleBaseUrl Remote module base URL.
 * @param {String} remoteModuleContainer.remoteEntryUrl Remote entry URL.
 * @param {String} remoteModuleContainer.remoteEntryResponseText Remote entry response text.
 * @param {String} remoteModuleContainer.scope Module container scope.
 * @returns The function `evalRemoteModuleContainer` returns the result of evaluating
 * the code provided in the `remoteEntryResponseText` within the specified `scope`.
 */
function evalRemoteModuleContainer({
    remoteModuleBaseUrl,
    remoteEntryUrl,
    remoteEntryResponseText,
    scope,
    clientRemoteModuleBaseUrl,
    moduleReference,
}) {
    try {
        // Using eval() instead of vm.runInThisContext() as that only have access to global object,
        // not the local executing scope. It forces us to assign local things into the global object
        // such as "global.require = require". vm.runInThisContext() has better error messages.
        //
        // Also creates problems in unit tests when we have remote scripts execute a mocked global
        // function. Assigned globals in test files could not be visible in scripts executed in
        // the vm for some reason...
        //
        // https://nodejs.org/docs/latest-v14.x/api/vm.html#vm_vm_runinthiscontext_code_options
        // eslint-disable-next-line no-eval
        return eval(
            // baseUri          is used by webpack runtime (RemoteChunkLoadingRuntimeModule)
            //                  to request the app chunks it needs
            // clientBaseUri    is used by web-platform-public-path to set the public path
            `var baseUri = '${remoteModuleBaseUrl}';\nvar clientBaseUri = '${clientRemoteModuleBaseUrl}';\n${remoteEntryResponseText}\n ${scope}`,
        );
    } catch (error) {
        const customEvalError = new ImportRemoteError(
            `failed to parse and evaluate remote entry code. URL: ${remoteEntryUrl}. ${error.name} ${error.message}`,
            {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryExecution,
                cause: error,
                executionContext:
                    error.executionContext ||
                    getExecutionContextString(moduleReference),
            },
        );

        // check if error has stack
        if (!error.stack) {
            throw customEvalError;
        }

        // get lines of stacktrace
        const stacktrace = error.stack.split('\n');
        if (!stacktrace[1]) {
            throw customEvalError;
        }

        // line where eval anonymous script error id defined
        const evalTrace = stacktrace[1];

        // check if eval trace has information about anonymous script error
        const hasAnonymousError = evalTrace.includes('<anonymous>:');
        if (!hasAnonymousError) {
            throw customEvalError;
        }

        const [, evalError] = evalTrace.split('<anonymous>:');
        // get line and char where error happened
        const [errorLine, errorChar] = evalError.slice(0, -1).split(':');

        // remove line of code we added at evaluated string
        throw new ImportRemoteError(
            `failed to parse and evaluate remote entry code. URL: ${remoteEntryUrl}:${
                errorLine - 1
            }:${errorChar}. ${error.name} ${error.message}`,
            {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryExecution,
                cause: error,
                executionContext:
                    error.executionContext ||
                    getExecutionContextString(moduleReference),
            },
        );
    }
}

/**
 * The function initializes a remote container using a specified URL and throws an error if
 * initialization fails.
 * @param {Object} container - The `container` parameter is an object that represents the remote
 * container. It is used to load and initialize the remote modules.
 * @param {String} remoteEntryUrl - The `remoteEntryUrl` parameter is the URL of the remote
 * entry file. This file is typically generated by a remote application and contains
 * information about the shared modules that can be loaded from the remote application.
 */
async function initContainer(container, remoteEntryUrl, moduleReference) {
    try {
        await container.init(__webpack_share_scopes__.default); // eslint-disable-line no-undef
    } catch (error) {
        throw new ImportRemoteError(
            `failed to initialize remote container. URL: ${remoteEntryUrl}`,
            {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryExecution,
                cause: error,
                executionContext:
                    error.executionContext ||
                    getExecutionContextString(moduleReference),
            },
        );
    }
}

/**
 * The function loads a remote container and initializes it.
 * @param {String} remoteEntryUrl - The URL of the remote entry file. This file contains information about the
 * remote modules that can be loaded dynamically.
 * @param {String} scope - The `scope` parameter is a string that represents the scope or namespace in which the
 * remote modules will be loaded. It is used to isolate the loaded modules from the global scope and
 * prevent conflicts with other modules or variables in the application.
 * @param {String} moduleReference - the <subApp>/<module> referenece
 * @returns {Object} The `loadContainer` function returns a container object.
 */
async function loadContainer(
    remoteEntryUrl,
    scope,
    clientRemoteEntryUrl,
    moduleReference,
) {
    const remoteEntryResponseText = await getRemoteEntryResponseText(
        remoteEntryUrl,
        moduleReference,
    );
    const remoteModuleBaseUrl = getRemoteModuleBaseUrl(
        remoteEntryUrl,
        moduleReference,
    );
    const clientRemoteModuleBaseUrl = getRemoteModuleBaseUrl(
        clientRemoteEntryUrl,
        moduleReference,
    );
    const container = evalRemoteModuleContainer({
        remoteEntryResponseText,
        remoteEntryUrl,
        remoteModuleBaseUrl,
        scope,
        clientRemoteModuleBaseUrl,
        moduleReference,
    });
    await initContainer(container, remoteEntryUrl, moduleReference);
    return container;
}

/**
 *
 * @param {object} options
 * @param {string} options.remoteEntryUrl
 * @param {string} options.clientRemoteEntryUrl
 * @param {string} options.modulePath
 * @param {string} options.scope
 * @param {string} [options.version]
 * @param {boolean} [options.isLocalOverride]
 * @param {boolean} [options.hasOverrideCookie]
 * @param {number} [options.moduleTTL]
 * @returns {Promise<any>}
 */
async function loadModuleOnServer(options) {
    const {
        version = 'unknown',
        remoteEntryUrl,
        clientRemoteEntryUrl,
        scope,
        modulePath,
        isLocalOverride,
        hasOverrideCookie,
        moduleTTL,
        moduleReference,
    } = options;

    const moduleCacheKey = getModuleCacheKey(remoteEntryUrl, modulePath);
    const cachedModule = localCache.get(moduleCacheKey);
    if (cachedModule && !hasOverrideCookie && !isLocalOverride) {
        return cachedModule;
    }

    /*
     * The below code is checking if a container is already cached. If the container
     * is found in the cache, it is retrieved. If not, the container is loaded from a remote entry URL
     * and stored in the cache. If the container is loaded for the first time and certain conditions are
     * met (such as being in production mode and not having a local override), the container is also
     * stored in the cache for future use.
     */
    const containerCacheKey = getContainerCacheKey(remoteEntryUrl);
    const cachedContainer = localCache.get(containerCacheKey);
    const container =
        cachedContainer ??
        (await loadContainer(
            remoteEntryUrl,
            scope,
            clientRemoteEntryUrl,
            moduleReference,
        ));
    if (
        !cachedContainer &&
        container &&
        containerCacheKey &&
        isProduction() &&
        !isLocalOverride
    ) {
        localCache.put(containerCacheKey, container);
    }

    /*
     * set the baseURI webpack runtime global variable
     * the remote entry script will request the app chunks
     * it needs th
     */
    let Module;
    try {
        const factory = await container.get(modulePath);
        Module = await factory();

        // hoist sub app version for obverability
        // eslint-disable-next-line no-underscore-dangle
        Module.__version = !isLocalOverride ? version : 'overrided';
    } catch (error) {
        // Throw this error as its own type so Platform is aware that the scope and module name combination
        // does not exist. Error message: Module "./ModuleName" does not exist in container.
        if (
            error?.message &&
            error.message.match(/does not exist in container/i)
        ) {
            throw new ImportRemoteError(
                `failed to initialize remote module. Module not found in container. moduleName/path: "${modulePath}" .URL: ${remoteEntryUrl}`,
                {
                    type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryModuleNotFound,
                    cause: error,
                    executionContext:
                        error.executionContext ||
                        getExecutionContextString(moduleReference),
                },
            );
        }

        throw new ImportRemoteError(
            `failed to initialize remote module from container. moduleName/path: "${modulePath}" .URL: ${remoteEntryUrl}`,
            {
                type: IMPORT_REMOTE_ERROR_TYPES.remoteEntryExecution,
                cause: error,
                executionContext:
                    error.executionContext ||
                    getExecutionContextString(moduleReference),
            },
        );
    }

    // Only enable caching in production envs to allow for easier local development
    // set moduleTTL only if a non zero value is set, else no TTL
    if (Module && moduleCacheKey && isProduction() && !isLocalOverride) {
        const moduleCacheTTL =
            moduleTTL > 0 ? moduleTTL : DEFAULT_MODULE_CACHE_TTL_MS;
        localCache.put(moduleCacheKey, Module, moduleCacheTTL, (key) => {
            console.info(`[ImportRemote] [ModuleCache] evicting module ${key}`);
        });
        console.info(
            `[ImportRemote] [ModuleCache] caching module ${moduleCacheKey}, ttl: ${moduleCacheTTL}ms`,
        );
    }

    return Module;
}

function getProcessTimeMS() {
    return Date.now();
}

function getElapseTimeMS(startTime) {
    const endTime = getProcessTimeMS();
    return endTime - startTime;
}

let manifestUpdateIntervalId;
let startErrorMS = 0;
function isErrorDurationLongerThanThreshold(startErorMS) {
    return getElapseTimeMS(startErorMS) > TWENTY_FIVE_SECONDS_IN_MS;
}

function setupManifestUpdateInterval() {
    if (!manifestUpdateIntervalId) {
        manifestUpdateIntervalId = setInterval(async () => {
            const allModules = Object.keys(MODULES_TO_UPDATE);

            const result = [];
            const env = getImportRemoteContext()?.env;

            // Do update sequentially to avoid overloading the service
            // Note: Later, we should introduce a batch endpoints and do a single fetch instead.
            for (let i = 0; i < allModules.length; i += 1) {
                const moduleName = allModules[i];

                try {
                    // eslint-disable-next-line no-await-in-loop
                    await internalFetchSubAppManifest(
                        moduleName,
                        moduleName,
                        env,
                    );
                    result.push(moduleName);
                } catch (error) {
                    if (startErrorMS <= 0) {
                        startErrorMS = getProcessTimeMS();
                    }

                    /**
                     * Log error if the error has been longer than the threshold,
                     * otherwise, warning intead.
                     */
                    const log = isErrorDurationLongerThanThreshold(startErrorMS)
                        ? console.error
                        : console.warn;
                    log(
                        `[ImportRemoteUpdateInterval] failed to update module manifest for module: ${moduleName}`,
                        error,
                    );

                    if (error instanceof ImportRemoteError && error.cause) {
                        const message = error.cause.toString();
                        const isInvalidSubAppName =
                            message.includes('SchemaValidationError') ||
                            message.includes('SubAppManifestCache') ||
                            message.includes('specificVersionSubAppHandler');

                        if (isInvalidSubAppName) {
                            delete MODULES_TO_UPDATE[moduleName];
                        }
                    }
                }
            }

            // Reset error timer if we have no error
            if (result.length === allModules.length) {
                startErrorMS = 0;
            }
        }, FIVE_SECONDS_UPDATE_LOOP_MS);

        const cancelInterval = (signal) => () => {
            console.info(
                `[ImportRemoteUpdateInterval] [${signal}] cancel update module manifest interval`,
            );
            if (manifestUpdateIntervalId) {
                clearInterval(manifestUpdateIntervalId);
                manifestUpdateIntervalId = null;
            }
        };

        process.on('exit', cancelInterval('exit'));
        process.on('SIGINT', cancelInterval('SIGINT'));
        process.on('SIGTERM', cancelInterval('SIGTERM'));
    }
}

/**
 * Imports a remote module directly, without checking with shopper platform.
 * @param  {object} options  Options object.
 * @param  {string} options.remoteEntryUrl  The URL to the remote entry script.
 * @param  {string} options.clientRemoteEntryUrl  The URL to the remote entry script for the client (used for server side import only).
 * @param  {string} options.modulePath  The module to import from the remote.
 * @param  {string} options.scope  The scope that the remote registers with webpack.
 * @return {Promise<any>} A promise that resolves to the imported module
 */
function remoteWithConfig(_a) {
    return __awaiter(this, arguments, void 0, function* ({ remoteEntryUrl, clientRemoteEntryUrl, modulePath, scope, }) {
        const loadModule = isNodeEnv() ? loadModuleOnServer : loadModuleOnClient;
        return loadModule({
            remoteEntryUrl,
            clientRemoteEntryUrl: clientRemoteEntryUrl !== null && clientRemoteEntryUrl !== void 0 ? clientRemoteEntryUrl : remoteEntryUrl,
            modulePath,
            scope,
        });
    });
}
/**
 * Import a remote subApp with redundency and validation checks. Caches module imports in window.importedRemotes to prevent redundent requests.
 * @param  {string} reference Contains a subAppName registered with shopper platform, and a module to import from that subapp in the format ``${subAppName}/${module}``.
 * @param {object} [options] Options object.
 * @param {Env} [options.env] One of: `dev`, `test`, or `production`. Represents which version of Shopper Platform to integrate with. Defaults to `production`.
 * @param {VersionOverride} [options.version] Retrieve a specific module version.
 * @param {number} [options.serverModuleTTL] Time in ms to cache compiled module. **SERVER ONLY**. Defaults to no TTL.
 * @return {Promise<any>} A promise that resolves to the imported module from the subapp.
 */
function remote(reference_1) {
    return __awaiter(this, arguments, void 0, function* (reference, options = {}) {
        const { env, version, serverModuleTTL } = options, errorOptions = __rest(options, ["env", "version", "serverModuleTTL"]);
        try {
            // Check that a remoteReference is provided
            if (!reference) {
                throw new ImportRemoteError(`failed to load federated module with missing subAppName/Module`);
            }
            // Check that the remoteReference is formatted with both a subAppName and a Module.
            if (!reference.match(/.+\/.+/)) {
                throw new ImportRemoteError(`invalid subAppName/Module reference: "${reference}"`);
            }
            if (isNodeEnv()) {
                if (!isLocal() && !isCI()) {
                    setupManifestUpdateInterval();
                }
                return yield remoteOnServer(reference, {
                    env,
                    version,
                    moduleTTL: serverModuleTTL,
                });
            }
            return yield remoteOnClient(reference, {
                env,
                version,
            });
        }
        catch (error) {
            if ('onError' in errorOptions && errorOptions.onError) {
                const remoteError = getUncoughtError(error);
                return errorOptions.onError({
                    error: remoteError,
                    DefaultFallback: DefaultNullReturn,
                });
            }
            if ('throwError' in errorOptions && errorOptions.throwError) {
                throw getUncoughtError(error);
            }
            return createNullReturnModule(reference, error);
        }
    });
}

export { IMPORT_REMOTE_ERROR_TYPES, WINDOW_MANIFEST_CACHE_KEY, getRemoteManifestOnClient, getRemoteManifestOnServer, remote, remoteWithConfig };
